<script lang="ts">
	import { onDestroy, untrack } from 'svelte'
	import { fade } from 'svelte/transition'
	import { HandleBlockClick } from '$lib/utils/mapRoutingState'
	import blockState from '$lib/stores/block-state'
	import { browser } from '$app/environment'
	import type { MapViewBlockFieldsFragment } from '$lib/queries/fragments/generated/MapViewBlockFields'
	import { isMapLoaded, mapStore } from '$lib/stores/map-state'
	import { urlState } from '$lib/stores/url-state'
	import {
		addPin,
		addRegionMarker,
		clearMarkers,
		createCustomRegionPolygon,
		drawPolygon,
		setSearchArea,
	} from '$lib/map/map-markers'
	import { filterPolygonsByBounds, isBlockHighlighted, setupClickListener } from '$lib/utils/map'
	import debounce from 'lodash-es/debounce'
	import GoogleSearchMarker from './GoogleSearchMarker.svelte'
	import PinIcon from '../icons/PinIcon.svelte'
	import { stopPropagation } from '$lib/utils/clickHelpers'

	interface Props {
		customRegionBlocks?: MapViewBlockFieldsFragment[]
		isEmptyState?: boolean
	}

	let { customRegionBlocks = [], isEmptyState = false }: Props = $props()

	// Store subscriptions with destructuring
	let { map, zoom, bounds, isMoving } = $derived($mapStore)
	let {
		tripId,
		panel: panelState,
		parentBlockId,
		isHighlights,
		itineraryId,
		expandedBlockId,
		isPublic,
		blockId: selectedBlockId,
	} = $derived($urlState)

	let mapState = $derived($blockState?.mapState)

	let isSearchMode = $derived(
		mapState === 'search' ||
			isEmptyState ||
			['place', 'pin', 'region', 'destination'].includes(panelState ?? ''),
	)

	// Simplified state management
	let clickListener: google.maps.MapsEventListener | null = $state(null)
	let customRegionPolygons: google.maps.Polygon[] = []
	let polygon: google.maps.Polygon | null = $state(null)

	let searchSelected = $derived($blockState.searchSelected ?? false)
	let shouldHighlight = $derived(isHighlights || selectedBlockId != null || expandedBlockId != null)
	let markers = $derived($blockState?.mapMarkers ?? [])
	let customPin = $derived(markers[0] ?? null)

	// Unified polygon state management
	const updatePolygonState = (poly: google.maps.Polygon, opacity: number) => {
		poly.setOptions({ fillOpacity: opacity })
	}

	const getPolygonOpacity = (block: MapViewBlockFieldsFragment, isHovered: boolean): number => {
		if (isHovered) return 0.25

		const isActive =
			selectedBlockId === block?.id || parentBlockId === block?.id || expandedBlockId === block?.id

		const isHighlighted = isBlockHighlighted(
			block,
			selectedBlockId,
			parentBlockId,
			expandedBlockId,
			isActive,
			isHighlights,
		)

		if (isHighlighted) return 0.15
		if (shouldHighlight) return 0.05
		return 0.08
	}

	// Debounced handlers
	const updateHoverPolyStates = debounce(() => {
		customRegionPolygons?.forEach((poly) => {
			const id = poly['id']
			const block = customRegionBlocks?.find((b) => b.id === id)
			if (!block) return

			const opacity = getPolygonOpacity(block, id === $blockState.hoveringPlaceId)

			updatePolygonState(poly, opacity)
		})
	}, 50)

	// Cleanup functions
	const clearCustomRegions = () => {
		customRegionPolygons.forEach((p) => {
			// The mouseMoveListener is now handled in the clearPolygons function in map-markers.ts
			google.maps.event.clearInstanceListeners(p)
			p?.setMap(null)
		})
		customRegionPolygons = []
	}

	const clearPolygon = () => {
		if (polygon) {
			polygon.setMap(null)
			polygon = null
			clearMarkers()
		}
	}

	// Handle block interactions
	const openBlockPage = (blockId: string) => {
		HandleBlockClick(blockId, tripId, parentBlockId, browser, isPublic, itineraryId).then(() =>
			blockState.clearAllCustom(),
		)
	}

	// Update polygons when relevant state changes
	const updatePolygons = debounce((blocks, currentMap, currentBounds, currentZoom) => {
		clearCustomRegions()
		// Only create custom regions when zoom >= 8
		if (currentZoom >= 8) {
			const visiblePolygons = filterPolygonsByBounds(blocks, currentBounds, currentZoom)
			customRegionPolygons = visiblePolygons.map((block) => {
				const isActive =
					selectedBlockId === block?.id ||
					parentBlockId === block?.id ||
					expandedBlockId === block?.id

				const isHighlighted = isBlockHighlighted(
					block,
					selectedBlockId,
					parentBlockId,
					expandedBlockId,
					isActive,
					isHighlights,
				)

				return createCustomRegionPolygon(
					block,
					currentMap,
					openBlockPage,
					isActive,
					isHighlighted,
					shouldHighlight,
				)
			})
		}
	}, 300)

	// $: if (map && $isMapLoaded && zoom && markers && !isMoving && !polygon) {
	// 	polygon = drawPolygon(markers, map, polygon)
	// }

	// 1. Single effect for map click handling
	$effect(() => {
		// Clear listener if map isn't ready or we're moving
		if (!map || !$isMapLoaded || isMoving || !['region', 'pin'].includes(panelState)) {
			if (clickListener) {
				google.maps.event.removeListener(clickListener)
				clickListener = null
			}
			return
		}

		// Set up click listener
		clickListener = setupClickListener(map, (mapsMouseEvent) => {
			const clickedLatLng = mapsMouseEvent.latLng.toJSON()

			if (panelState === 'pin') {
				blockState.setMapMarkers([]) // Clear existing markers
				addPin(clickedLatLng)
			} else if (panelState === 'region') {
				const currentMarkers = untrack(() => markers)
				addRegionMarker(currentMarkers, clickedLatLng)
			}
		})

		// Cleanup
		return () => {
			if (clickListener) {
				google.maps.event.removeListener(clickListener)
				clickListener = null
			}
		}
	})

	$effect(() => {
		// Prevent running when not needed
		if (!map || !$isMapLoaded || !markers.length || panelState !== 'region') {
			if (polygon) {
				polygon.setMap(null)
				polygon = null
			}
			return
		}

		// Track previous marker positions to detect real changes
		const currentPositions = markers.map((m) => `${m.lat},${m.lng}`).join('|')

		// Store previous positions to compare
		const previousPositions = polygon?.get('positions')

		// Only redraw if positions actually changed
		if (currentPositions !== previousPositions) {
			polygon = drawPolygon(markers, map, polygon)
			// Store new positions for next comparison
			polygon?.set('positions', currentPositions)
		}
	})

	// 2. Single effect for custom regions
	let previousItineraryId: string | null = null
	$effect(() => {
		// Clear regions if itinerary changes
		if (itineraryId !== previousItineraryId) {
			clearCustomRegions()
			previousItineraryId = itineraryId
		}

		// Only handle custom regions when not in pin/region mode and not in search mode
		if (!map || !$isMapLoaded || ['region', 'pin'].includes(panelState) || isSearchMode) {
			clearCustomRegions()
			return
		}

		// Clear custom regions if zoom is less than 8
		if (zoom < 8) {
			clearCustomRegions()
			return
		}

		// Update custom regions if we have sufficient zoom
		if (customRegionBlocks.length > 0) {
			const visiblePolygons = filterPolygonsByBounds(customRegionBlocks, bounds, zoom)

			clearCustomRegions() // Clear existing before adding new

			customRegionPolygons = visiblePolygons.map((block) => {
				const isActive = [selectedBlockId, parentBlockId, expandedBlockId].includes(block?.id)
				const isHighlighted = isBlockHighlighted(
					block,
					selectedBlockId,
					parentBlockId,
					expandedBlockId,
					isActive,
					isHighlights,
				)

				return createCustomRegionPolygon(
					block,
					map,
					openBlockPage,
					isActive,
					isHighlighted,
					shouldHighlight,
				)
			})
		}
	})

	// 3. Single effect for polygon hover states
	$effect(() => {
		const relevantStateChanged = [
			parentBlockId,
			selectedBlockId,
			$blockState.hoveringPlaceId || $blockState.hoveringPlaceId === null,
			shouldHighlight,
		].some(Boolean)

		if (relevantStateChanged) {
			customRegionPolygons?.forEach((poly) => {
				const id = poly['id']
				const block = customRegionBlocks?.find((b) => b.id === id)
				if (!block) return

				const opacity = getPolygonOpacity(block, id === $blockState.hoveringPlaceId)
				updatePolygonState(poly, opacity)
			})
		}
	})

	// Cleanup on destroy
	onDestroy(() => {
		clearCustomRegions()
		clearPolygon()
		updatePolygons.cancel()
		updateHoverPolyStates.cancel()
		if (clickListener) {
			google.maps.event.removeListener(clickListener)
		}
	})
</script>

{#if ['region', 'pin', 'place'].includes(panelState)}
	<div class="absolute w-full top-2 h-16 flex items-center justify-center gap-x-2">
		{#if panelState === 'place' && !searchSelected}
			<button
				transition:fade={{ duration: 120 }}
				onclick={stopPropagation(() => setSearchArea(map))}
				class="absolute rounded-md cursor-pointer text-xs lg:text-sm font-semibold z-20 py-2 px-4 shadow-md border bg-brand-gray-6 hover:bg-opacity-80 text-white border-transparent touch-manipulation"
			>
				<span>Search This Area</span>
			</button>
		{:else if panelState !== 'place'}
			<button
				transition:fade={{ duration: 120 }}
				disabled={markers.length === 0}
				onclick={stopPropagation(clearPolygon)}
				class={`absolute cursor-pointer text-xs lg:text-sm font-semibold z-20 py-2 px-4 shadow-md border ${
					markers.length > 0
						? 'bg-brand-gray-6 hover:bg-opacity-80 text-white border-transparent'
						: 'bg-brand-gray-0 text-brand-gray-6 border-brand-gray-6'
				} rounded-md transform transition-colors touch-manipulation`}
			>
				<span>
					{#if markers.length > 0}
						Clear {panelState === 'region' ? 'Polygon' : 'Pin'}
					{:else}
						Click to {panelState === 'region' ? 'Draw a Polygon' : 'Drop Pin'}
					{/if}
				</span>
			</button>
		{/if}
	</div>
{/if}

{#if $isMapLoaded && markers.length === 1 && panelState === 'region'}
	<GoogleSearchMarker
		isVisable={zoom >= 8}
		{map}
		isActive={false}
		latitude={markers[0]?.lat}
		longitude={markers[0]?.lng}
	>
		<div class="rounded-full h-1 w-1 bg-black"></div>
	</GoogleSearchMarker>
{/if}

{#key customPin?.lat + customPin?.lng}
	{#if $isMapLoaded && customPin && panelState === 'pin'}
		<div class="overflow-hidden">
			<GoogleSearchMarker
				isVisable={zoom >= 8}
				{map}
				isActive={false}
				latitude={customPin?.lat}
				longitude={customPin?.lng}
			>
				<PinIcon class="h-10 w-10 m-auto text-purple-600" />
			</GoogleSearchMarker>
		</div>
	{/if}
{/key}
